import { Vec3, v3, Vec2, v2 } from 'cc';

export const SEG: number = 1000;

/** 取樣點對應的係數表索引超出範圍 (舊算法在此情況下會得到 NaN, 經 Vec2 建構後變為 0) */
export const INVALID_STEP = 0xffff;

/**
 * calculateRunTimePoints 結果的精簡描述: 不保存座標, 只保存重建座標所需的資訊,
 * 由 RunPath 以相同的算式即時算出與原本完全一致的座標
 */
export type RunTimeSamples = {
    count: number; // 取樣點總數 (含結尾位置)
    endsOnly: boolean; // 總取樣數為0, 只有起點與終點
    segmentStart: Uint32Array; // 各分段第一個取樣點的索引
    segmentIsLine: Uint8Array; // 各分段是否以直線處理
    lineSampleNumber: Float64Array; // 直線分段的取樣數 (內插比例的分母)
    steps: Uint16Array; // 曲線分段各取樣點的係數表索引
    params: Float32Array; // 曲線分段各取樣點未量化的曲線參數 t (供平滑移動使用)
};

let c0: number[] = [];
let c1: number[] = [];
let c2: number[] = [];
let c3: number[] = [];

export default class SplineInterpolator {
    static readonly DEG_PER_RADIUS: number = 57.2957;
    static coefInitSeg: boolean;

    public static getPointDistance(p1: Vec3 | Vec2, p2: Vec3 | Vec2): number {
        return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
    }

    public static bezierAt(a: number, b: number, c: number, d: number, step: number): number {
        return c0[step] * a + c1[step] * b + c2[step] * c + c3[step] * d;
    }

    // 依照t計算speed
    public static speedAt(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, t: number): number {
        const vx: number = 3 * (1 - t) * (1 - t) * (p1.x - p0.x) + 6 * (1 - t) * t * (p2.x - p1.x) + 3 * t * t * (p3.x - p2.x);
        const vy: number = 3 * (1 - t) * (1 - t) * (p1.y - p0.y) + 6 * (1 - t) * t * (p2.y - p1.y) + 3 * t * t * (p3.y - p2.y);
        return Math.sqrt(vx ** 2 + vy ** 2);
    }

    public static coefficientInit(segment: number) {
        c0 = [];
        c1 = [];
        c2 = [];
        c3 = [];

        for (let step = 0; step <= segment; step++) {
            const t: number = step / segment;
            c0[step] = (1 - t) * (1 - t) * (1 - t); // * p0
            c1[step] = 3 * (1 - t) * (1 - t) * t; // * p1
            c2[step] = 3 * (1 - t) * t * t; // * p2
            c3[step] = t * t * t; // * p3;
        }
        SplineInterpolator.coefInitSeg = true;
    }

    // 計算實際RunTime的路徑(點形式)
    public static calculateRunTimePoints(controlPoints: Vec3[], totalSampleNumber: number, segmentLengths: number[], segmentSampleNumbers: number[]): Vec2[] {
        const result: Vec2[] = [];

        // 若總取樣數為0，則只加入起點與終點
        if (totalSampleNumber === 0) {
            result.push(controlPoints[0].toVec2());
            result.push(controlPoints[controlPoints.length - 1].toVec2());
        } else {
            for (let pairs = 0; pairs < segmentSampleNumbers.length; pairs++) {
                const p0: Vec3 = controlPoints[pairs * 3];
                const p1: Vec3 = controlPoints[pairs * 3 + 1];
                const p2: Vec3 = controlPoints[pairs * 3 + 2];
                const p3: Vec3 = controlPoints[pairs * 3 + 3];

                // 為直線
                if (Math.abs(p0.x - p1.x) <= 4 && Math.abs(p0.y - p1.y) <= 4 && Math.abs(p2.x - p3.x) <= 4 && Math.abs(p2.y - p3.y) <= 4) {
                    const segSampleNumber = segmentSampleNumbers[pairs];

                    for (let index = 0; index < segmentSampleNumbers[pairs]; index++) {
                        const scale: number = index / segSampleNumber;
                        result.push(v2(p0.x * (1 - scale) + p3.x * scale, p0.y * (1 - scale) + p3.y * scale));
                    }
                } else {
                    // 為曲線
                    const arcResult: number[] = SplineInterpolator.arcParameter(p0, p1, p2, p3, segmentLengths[pairs], segmentSampleNumbers[pairs]);
                    arcResult.forEach((t) => {
                        if (t > 1) {
                            t = 1;
                        }
                        result.push(SplineInterpolator.interpolationPoint(p0, p1, p2, p3, t * SEG));
                    });
                }
            }
            // 加入結尾位置
            result.push(controlPoints[controlPoints.length - 1].toVec2());
        }
        return result;
    }

    /**
     * 與 calculateRunTimePoints 相同的取樣流程, 但只記錄每個取樣點的來源 (直線的第幾點 / 曲線的係數表索引與曲線參數)
     * 座標改由 RunPath 取得
     */
    public static calculateRunTimeSamples(
        controlPoints: Vec3[],
        totalSampleNumber: number,
        segmentLengths: number[],
        segmentSampleNumbers: number[],
    ): RunTimeSamples {
        // 若總取樣數為0，則只加入起點與終點
        if (totalSampleNumber === 0) {
            return {
                count: 2,
                endsOnly: true,
                segmentStart: new Uint32Array(1),
                segmentIsLine: new Uint8Array(0),
                lineSampleNumber: new Float64Array(0),
                steps: new Uint16Array(0),
                params: new Float32Array(0),
            };
        }
        const segmentCount = segmentSampleNumbers.length;
        const segmentStart = new Uint32Array(segmentCount + 1);
        const segmentIsLine = new Uint8Array(segmentCount);
        const lineSampleNumber = new Float64Array(segmentCount);
        const curveSteps: number[][] = [];
        const curveParams: number[][] = [];
        let count = 0;
        for (let pairs = 0; pairs < segmentCount; pairs++) {
            const p0: Vec3 = controlPoints[pairs * 3];
            const p1: Vec3 = controlPoints[pairs * 3 + 1];
            const p2: Vec3 = controlPoints[pairs * 3 + 2];
            const p3: Vec3 = controlPoints[pairs * 3 + 3];
            segmentStart[pairs] = count;

            // 為直線
            if (Math.abs(p0.x - p1.x) <= 4 && Math.abs(p0.y - p1.y) <= 4 && Math.abs(p2.x - p3.x) <= 4 && Math.abs(p2.y - p3.y) <= 4) {
                segmentIsLine[pairs] = 1;
                lineSampleNumber[pairs] = segmentSampleNumbers[pairs];
                for (let index = 0; index < segmentSampleNumbers[pairs]; index++) {
                    count++;
                }
            } else {
                // 為曲線
                const arcResult: number[] = SplineInterpolator.arcParameter(p0, p1, p2, p3, segmentLengths[pairs], segmentSampleNumbers[pairs]);
                const params = arcResult.map((t) => (t > 1 ? 1 : t));
                const steps = params.map((t) => {
                    const step = Math.floor(t * SEG);
                    return step >= 0 && step <= SEG ? step : INVALID_STEP;
                });
                curveSteps[pairs] = steps;
                curveParams[pairs] = params;
                count += steps.length;
            }
        }
        segmentStart[segmentCount] = count;
        const steps = new Uint16Array(count);
        const params = new Float32Array(count);
        curveSteps.forEach((list, pairs) => {
            steps.set(list, segmentStart[pairs]);
            params.set(curveParams[pairs], segmentStart[pairs]);
        });
        // 加入結尾位置
        count++;
        return { count, endsOnly: false, segmentStart, segmentIsLine, lineSampleNumber, steps, params };
    }

    // 計算實際RunTime路徑的角度
    public static calculateRunTimeAngles(points: Vec3[]): number[] {
        const result: number[] = [];
        // 若傳入的vector為空，則直接回傳
        if (points.length <= 0) {
            return result;
        }

        // 加入角度變化
        let prevAngle = 0;
        for (let j = 0; j < points.length - 1; j++) {
            const atan = Math.atan2(points[j + 1].y - points[j].y, points[j + 1].x - points[j].x);
            const angle =
                atan === 0 ? prevAngle : 1 * Math.atan2(points[j + 1].y - points[j].y, points[j + 1].x - points[j].x) * SplineInterpolator.DEG_PER_RADIUS;
            prevAngle = angle;
            result.push(angle);
        }
        // 加入結尾的角度
        result.push(
            -1 *
                Math.atan2(points[points.length - 1].y - points[points.length - 2].y, points[points.length - 1].x - points[points.length - 2].x) *
                SplineInterpolator.DEG_PER_RADIUS,
        );

        return result;
    }

    // 生成等距離的路徑點資料
    public static arcParameter(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, arcLength: number, sampleNumber: number): number[] {
        const result: number[] = [];
        const h: number = arcLength / sampleNumber;
        let t: number = 0.0;

        // 加入起點
        result.push(0);

        // 若該曲線開頭趨近於直線，則進行特殊的初始化動作
        if (Math.abs(p0.x - p1.x) + Math.abs(p0.y - p1.y) <= 10) {
            const delta: number = 1 / sampleNumber / 3;
            t = 1 / sampleNumber;

            let last: Vec2 | Vec3 = p0;
            let now: Vec2 = SplineInterpolator.interpolationPoint(p0, p1, p2, p3, SEG * t);

            let distance: number = SplineInterpolator.getPointDistance(last, now);
            while (Math.abs(distance - h) / h > 0.05) {
                if (distance > h) {
                    t -= delta / 2;
                    break;
                } else {
                    // t 已超出係數表: 之後取到的點都相同 (NaN 經 Vec2 轉為 0), 距離不再變化, 迴圈永遠不會結束
                    if (Math.floor(SEG * t) > SEG) {
                        t = 1;
                        break;
                    }
                    last = now;
                    t += delta;
                    now = SplineInterpolator.interpolationPoint(p0, p1, p2, p3, SEG * t);
                    distance += SplineInterpolator.getPointDistance(last, now);
                }
            }
            result.push(t);
            sampleNumber--;
        }

        for (let index = 1; index < sampleNumber; index++) {
            const k1: number = h / SplineInterpolator.speedAt(p0, p1, p2, p3, t);
            const k2: number = h / SplineInterpolator.speedAt(p0, p1, p2, p3, t + k1 / 2);
            const k3: number = h / SplineInterpolator.speedAt(p0, p1, p2, p3, t + k2 / 2);
            const k4: number = h / SplineInterpolator.speedAt(p0, p1, p2, p3, t + k3);
            t += (k1 + 2 * (k2 + k3) + k4) / 6;
            result.push(t);
        }
        return result;
    }

    // 取得4個控制點的內插點
    public static bezierCurveInterpolation(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, segment: number): Vec3[] {
        const points: Vec3[] = [];
        if (!SplineInterpolator.coefInitSeg) {
            SplineInterpolator.coefficientInit(SEG);
        }

        // 加入起點
        points.push(p0);
        for (let step = 1; step < segment; step++) {
            const x: number = SplineInterpolator.bezierAt(p0.x, p1.x, p2.x, p3.x, Math.floor(step * (SEG / segment)));
            const y: number = SplineInterpolator.bezierAt(p0.y, p1.y, p2.y, p3.y, Math.floor(step * (SEG / segment)));
            points.push(v3(x, y));
        }
        points.push(p3);

        return points;
    }

    // 根據四個控制點與位置參數生成路徑點
    public static interpolationPoint(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, step: number): Vec2 {
        step = Math.floor(step);
        const x: number = SplineInterpolator.bezierAt(p0.x, p1.x, p2.x, p3.x, step);
        const y: number = SplineInterpolator.bezierAt(p0.y, p1.y, p2.y, p3.y, step);
        return v2(x, y);
    }

    // 分配曲線的每個分段的取樣數
    public static calculateRunTimeSampleNumber(pathPercents: number[], totalSampleNumber: number): number[] {
        // 沒有任何分段
        if (totalSampleNumber <= 0) {
            return [];
        }

        // 總取點數，以FPS為基準
        const sampleNumber: number[] = [];
        const remainder: number[] = [];

        // 根據四個控制點組成的曲線間的距離與總距離的比例分配取樣數
        pathPercents.forEach((pathPercent) => {
            const number: number = totalSampleNumber * pathPercent;
            sampleNumber.push(Math.floor(number));
            remainder.push(number - Math.floor(number));
        });

        // 檢查取樣數是否平分完畢
        let sum: number = 0;
        sampleNumber.forEach((n) => {
            sum += n;
        });
        // 若沒有，則再根據該Curve長度與現在平分到的取樣數算出的平均與總平均的差異下去分配
        if (sum !== totalSampleNumber) {
            const index: number[] = [];
            for (let i = 0; i < remainder.length; i++) {
                index.push(i);
            }

            let tmp: number;
            let j: number;
            // 排序餘數大小
            for (let i = 1; i < remainder.length; i++) {
                tmp = index[i];
                for (j = i; j > 0 && remainder[tmp] > remainder[index[j - 1]]; j--) {
                    index[j] = index[j - 1];
                }
                index[j] = tmp;
            }

            // 餘數最大的先加
            for (let i = 0; i < totalSampleNumber - sum; i++) {
                sampleNumber[index[i]]++;
            }
        }
        return sampleNumber;
    }

    // 取得貝茲其中一段Curve的弧長
    public static getArcLength(points: Vec3[]): number {
        let arcLength: number = 0.0;
        if (points.length === 0) {
            return 0.0;
        }

        for (let i = 0; i < points.length - 1; i++) {
            arcLength += SplineInterpolator.getPointDistance(points[i], points[i + 1]);
        }

        return arcLength;
    }
}
